
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Star, Eye } from 'lucide-react';
import { Game } from '@/types/game';

interface GameInfoProps {
  game: Game;
}

const GameInfo: React.FC<GameInfoProps> = ({ game }) => {
  const displayPlayCount = game.fakePlayCount ?? game.playCount ?? 0;

  return (
    <Card className="border-0 bg-white/70 backdrop-blur-sm shadow-lg">
      <CardContent className="p-6 md:p-8">
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {game.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <div className="flex items-center space-x-2 bg-yellow-50 px-3 py-1 rounded-full">
                <div className="flex items-center space-x-1">
                  <Star className="w-4 h-4 text-yellow-500" fill="currentColor" />
                  <span className="font-semibold text-gray-900">{game.rating.toFixed(1)}</span>
                  <span className="text-gray-600 text-sm">/ 5</span>
                </div>
                {game.ratingsCount && game.ratingsCount > 0 && (
                  <span className="text-gray-500 text-xs">
                    ({game.ratingsCount.toLocaleString()} votes)
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-1 bg-blue-50 px-3 py-1 rounded-full text-blue-700">
                <Eye className="w-4 h-4" />
                <span className="font-medium">{displayPlayCount.toLocaleString()}</span>
              </div>
              <span className="px-3 py-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-sm font-medium rounded-full capitalize">
                {game.category}
              </span>
              {game.trending && (
                <span className="px-3 py-1 bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm font-medium rounded-full animate-pulse">
                  🔥 Trending
                </span>
              )}
              {game.isNew && (
                <span className="px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm font-medium rounded-full">
                  ✨ New
                </span>
              )}
            </div>
          </div>

          <div>
            <p className="text-gray-700 text-base md:text-lg leading-relaxed">
              {game.description}
            </p>
          </div>

          {game.tags && game.tags.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {game.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-full transition-colors cursor-pointer"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default GameInfo;
